@if (isAbortingTerminal) {
  <div
    class="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white px-10 text-center"
  >
    @if (!abortTerminalFailed) {
      <i class="icon icon-privacy animate-pulse text-5xl kiosk-xl:text-8xl"></i>
      <p class="mt-6 text-2xl font-bold kiosk-xl:text-5xl">Annulation en cours…</p>
    }

    @if (abortTerminalFailed) {
      <p class="text-2xl font-bold kiosk-xl:text-5xl">L'annulation n'a pas pu être finalisée</p>
      <p class="mt-4 text-base text-neutral-600 kiosk-xl:text-3xl">
        Veuillez vérifier que la transaction a bien été annulée sur le terminal de paiement avant de
        continuer.
      </p>
      <div class="mt-8">
        <app-button-atom
          size="XXL"
          thickSize="XXL"
          type="primary"
          label="Forcer le retour"
          (clickEventEmitter)="forceBackToChoice()"
        ></app-button-atom>
      </div>
    }
  </div>
}

@let customTerminalLayout =
  step === kioskPaymentMethodPickerStep.TERMINAL && hasCustomTerminalContent();

<div
  class="flex flex-col justify-between"
  [ngClass]="customTerminalLayout ? 'relative h-screen overflow-hidden' : 'min-h-screen'"
>

  <!--  Header -->

  <app-block-list [blockIdList]="header()"></app-block-list>
  @if (isLoading()) {
    <div class="flex flex-1 items-center justify-center">
      <app-payment-loading-screen-atom
        [textSizeClass]="'text-6xl'"
      ></app-payment-loading-screen-atom>
    </div>
  }

  <!-- When there is only one payment like ON SITE payment, always add the block in the DOM, because it trigger some payment logics -->

  <div
    class="mx-5 flex items-center justify-start gap-3 text-xl text-neutral-500"
    [class.invisible]="!(isOrderValidation$ | async) && !customTerminalLayout"
    [class.hidden]="!(isOrderValidation$ | async) && customTerminalLayout"
  >
    <span>{{ 'kiosk.cart.orderValidationInProgress' | translate }}</span>
    <img src="assets/images/spinner.svg" />
  </div>
  <div [class.my-5]="!customTerminalLayout">
    @for (message of errorMessages$ | async; track message) {
      <p class="mx-5 rounded bg-error-50 px-4 py-3 font-base text-error-700">
        {{ message }}
      </p>
    }
  </div>

  @let isOrderValid = !!(order$ | async)?.final_price;

  @if (isOrderValid) {
    <!--  Choices -->
    @if (step === kioskPaymentMethodPickerStep.CHOICE) {
      <div
        [class.hidden]="isLoading()"
        class="flex flex-grow flex-col items-center justify-center"
      >
        <!-- Choice heading -->
        <h1
          class="mb-16 text-center text-4xl font-bold kiosk-xl:mb-24 kiosk-xl:text-6xl"
          [style.color]="headingColor() | contentBuilderColor"
        >
          {{ headingChoosePaymentMethod() | translate }}
        </h1>
        <!-- Choice cards -->
        <div
          class="grid gap-12 px-5 kiosk-xl:px-10 kiosk-xl:pb-64"
          [ngClass]="{
            'grid-cols-2 ': paymentMethodCards.length > 1,
            'grid-cols-1 ': paymentMethodCards.length === 1,
          }"
        >
          @for (card of paymentMethodCards; track card) {
            <div>
              <div
                class="flex h-full w-full cursor-pointer flex-col items-center justify-center gap-8 rounded-lg border-4 border-solid px-10 py-12 text-center text-4.5xl"
                [style.color]="
                  selectedCard === card
                    ? (cardActiveTextColor() | contentBuilderColor)
                    : (cardInactiveTextColor() | contentBuilderColor)
                "
                [style.background-color]="
                  selectedCard === card
                    ? (cardActiveBackgroundColor() | contentBuilderColor)
                    : (cardInactiveBackgroundColor() | contentBuilderColor)
                "
                [style.border-color]="
                  selectedCard === card
                    ? (cardActiveTextColor() | contentBuilderColor)
                    : (cardInactiveTextColor() | contentBuilderColor)
                "
                (click)="selectCard(card)"
              >
                @if (card.icon) {
                  <i
                    class="icon text-3xl kiosk-xl:text-5xl"
                    [ngClass]="{
                      'icon-kiosk-icon-coins': card.icon === 'icon-kiosk-icon-coins',
                      'icon-kiosk-icon-card': card.icon === 'icon-kiosk-icon-card',
                    }"
                  ></i>
                }
                {{ card.label | translate }}
              </div>
            </div>
          }
        </div>
        <!-- Payment component without template (on site) -->
        @if (selectedCard && selectedCard.hasNextScreen === false) {
          @if (isPreviewMode()) {
            <div
              class="mt-12 flex w-full max-w-4xl flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed border-neutral-300 bg-neutral-50 px-8 py-10 text-center text-2xl text-neutral-700 kiosk-xl:text-4xl"
            >
              <div class="font-bold">{{ selectedCard.label | translate }}</div>
              <div class="text-lg kiosk-xl:text-3xl">
                Aperçu du panneau de paiement sans passage au composant réel.
              </div>
            </div>
          } @else {
            <app-component-factory
              [componentClass]="selectedCard.paymentMethod.panelComponent"
              [data]="getPanelData(selectedCard.paymentMethod)"
            ></app-component-factory>
          }
        }
      </div>
    }

    <!--  Choices -->
    @if (step === kioskPaymentMethodPickerStep.TERMINAL) {
      <div
        [class.hidden]="isLoading()"
        class="flex w-full flex-grow flex-col"
        [class.min-h-0]="hasCustomTerminalContent()"
        [class.items-center]="!hasCustomTerminalContent()"
        [class.justify-center]="!hasCustomTerminalContent()"
      >
        <div
          class="flex w-full flex-col"
          [class.min-h-0]="hasCustomTerminalContent()"
          [class.h-full]="hasCustomTerminalContent()"
          [ngClass]="
            hasCustomTerminalContent()
              ? ''
              : 'items-center justify-center gap-3 px-5 kiosk-xl:w-8/12 kiosk-xl:gap-12 kiosk-xl:p-0'
          "
          [style.color]="headingColor() | contentBuilderColor"
        >
          @if (hasCustomTerminalContent()) {
            <app-block-list [blockIdList]="terminalContent()"></app-block-list>
          }
          @if (selectedCard && selectedCard.hasNextScreen === true) {
            @if (isPreviewMode() && !hasCustomTerminalContent()) {
              <div
                class="flex w-full max-w-5xl flex-col items-center justify-center gap-6 rounded-2xl border-2 border-dashed border-neutral-300 bg-neutral-50 px-8 py-12 text-center text-2xl text-neutral-700 kiosk-xl:text-4xl"
              >
                <div class="font-bold">{{ selectedCard.label | translate }}</div>
                <div class="text-lg kiosk-xl:text-3xl">
                  {{ terminalInstructionMessage() | translate }}
                </div>
              </div>
            } @else if (!isPreviewMode()) {
              <app-component-factory
                [componentClass]="selectedCard.paymentMethod.panelComponent"
                [data]="getPanelData(selectedCard.paymentMethod)"
              ></app-component-factory>
            }
          }
          @if (!hasCustomTerminalContent() && payOnKioskText()) {
            <div class="text-center kiosk-xl:text-3xl">
              {{ payOnKioskText() | translate }}
            </div>
          }
          @if (!hasCustomTerminalContent() && displayPayOnKioskImage() && payOnKioskImage()) {
            <img
              class="max-h-[20vh] kiosk-xl:max-h-none"
              [src]="payOnKioskImage().url"
              alt="img"
            />
          }
        </div>
      </div>
    }
  }

  <!-- Footer -->
  @if (displayCancelButton()) {
    <div
      class="grid grid-cols-2 gap-10 p-5 kiosk-xl:p-10"
      [ngClass]="customTerminalLayout ? 'absolute inset-x-0 bottom-0 z-10' : ''"
      [class.hidden]="isLoading()"
    >
      <app-button-atom
        size="XXL"
        thickSize="XXL"
        type="kiosk-light"
        [label]="
          (cancelButtonLabel()
            | translateField: { defaultTranslationKey: 'cart-funnel.buttons.cancel' }) || ''
        "
        [overrideBgColor]="cancelButtonBackgroundColor()"
        [overrideTextColor]="cancelButtonTextColor()"
        [disabled]="!isCancelAvailable || isAbortingTerminal"
        (clickEventEmitter)="back()"
      ></app-button-atom>
    </div>
  }
</div>
